import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  InputBase,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AiPrompt, ConversationAttachment } from '@provision/entity-types';
import {
  type RefObject,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { AudioPlayer, DynamicIcon, Tooltip } from 'src/components';
import { LiveTranscriptRole } from 'src/components/Assistant/live-transcript/live-transcript.types';
import { renderTextWithHighlights } from 'src/components/Assistant/live-transcript/transcript-search-highlight';
import { AutocompletePopper } from 'src/components/AutocompletePopper/autocomplete-popper';
import {
  PartnerGroupRules,
  useCurrentUserProfile,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { useAiEmailFollowUp } from 'src/graphql/entities/ai-email-follow-up';
import { useAiPrompts } from 'src/graphql/entities/ai-prompt';
import { useMoveAttachmentToSiteFiles } from 'src/graphql/entities/files-upload';
import { getMoveAttachmentToSiteFilesPayload } from 'src/graphql/entities/files-upload.utils';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import { useGlobalStore } from 'src/store';
import { downloadFileFromUrl } from 'src/utils/file';
import { useCopyToClipboard, useNotifications } from 'src/utils/hooks';
import { isSupportedAudioAttachmentContentType } from './attachment-message-content.utils';
import {
  CALL_TRANSCRIPT_COPY_BUTTON_CLASS,
  callTranscriptBoxSx,
  callTranscriptCopyButtonSx,
  callTranscriptHeaderIconButtonSx,
  callTranscriptHeaderIconSx,
  callTranscriptHeaderSx,
  callTranscriptHeaderToggleIconSx,
  callTranscriptMessageBubbleSx,
  callTranscriptMessageContainerSx,
  callTranscriptMessageRowSx,
  callTranscriptMessagesContainerSx,
  callTranscriptMessagesWrapperSx,
  callTranscriptResumeButtonSx,
  callTranscriptSearchBarSx,
  callTranscriptSearchCountSx,
  callTranscriptSearchInputSx,
  callTranscriptSearchInputWrapSx,
  createCenteredIconSx,
} from './call-transcript.styles';
import {
  type CallTranscriptSegment,
  parseCallTranscriptSegments,
} from './call-transcript.utils';
import { FileAttachmentThumbnail } from './file-attachment-thumbnail';
import {
  CALL_TRANSCRIPT_SEGMENT_INDEX_ATTRIBUTE,
  useCallTranscriptAutoScroll,
} from './use-call-transcript-auto-scroll';
import { useCallTranscriptSearch } from './use-call-transcript-search';

const CallTranscriptMessage = ({
  segment,
  segmentIndex,
  showSpeakerChange,
  onCopy,
  searchQuery,
  matchOffset,
  currentMatchIndex,
}: {
  segment: CallTranscriptSegment;
  segmentIndex: number;
  showSpeakerChange: boolean;
  onCopy: (text: string) => void;
  searchQuery: string;
  matchOffset: number;
  currentMatchIndex: number;
}) => {
  const isAgent = segment.role === LiveTranscriptRole.Agent;

  return (
    <Stack
      {...{ [CALL_TRANSCRIPT_SEGMENT_INDEX_ATTRIBUTE]: segmentIndex }}
      sx={callTranscriptMessageContainerSx(isAgent)}
    >
      {showSpeakerChange ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
          {segment.speakerLabel}
          {segment.timestamp ? ` • ${segment.timestamp}` : ''}
        </Typography>
      ) : null}

      <Stack direction={isAgent ? 'row-reverse' : 'row'} sx={callTranscriptMessageRowSx}>
        <Stack sx={callTranscriptMessageBubbleSx(isAgent)}>
          <Typography variant="body2">
            {renderTextWithHighlights({
              text: segment.transcriptionText,
              query: searchQuery,
              matchOffset,
              currentMatchIndex,
            })}
          </Typography>
        </Stack>

        <IconButton
          className={CALL_TRANSCRIPT_COPY_BUTTON_CLASS}
          aria-label="Copy message"
          size="small"
          onClick={() => onCopy(segment.transcriptionText)}
          sx={callTranscriptCopyButtonSx}
        >
          <DynamicIcon name="content_copy" sx={createCenteredIconSx(0.95)} />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export const CallTranscriptionContainer = ({
  segments,
  scrollRef,
  onCopySegment,
  searchQuery,
  currentMatchIndex,
  matchOffsetBySegmentId,
  onScroll,
  onUserScrollIntent,
  canResumeAutoScroll = false,
  onResumeAutoScroll,
}: {
  segments: CallTranscriptSegment[];
  scrollRef: RefObject<HTMLDivElement | null>;
  onCopySegment: (text: string) => void;
  searchQuery: string;
  currentMatchIndex: number;
  matchOffsetBySegmentId: Map<string, number>;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
  onUserScrollIntent?: () => void;
  canResumeAutoScroll?: boolean;
  onResumeAutoScroll?: () => void;
}) => (
  <Box sx={callTranscriptMessagesWrapperSx}>
    <Stack
      ref={scrollRef}
      onScroll={onScroll}
      onWheel={onUserScrollIntent}
      onTouchMove={onUserScrollIntent}
      sx={callTranscriptMessagesContainerSx}
    >
      {segments.map((segment, index) => (
        <CallTranscriptMessage
          key={segment.id}
          segment={segment}
          segmentIndex={index}
          showSpeakerChange={index === 0 || segments[index - 1].role !== segment.role}
          onCopy={onCopySegment}
          searchQuery={searchQuery}
          matchOffset={matchOffsetBySegmentId.get(segment.id) ?? 0}
          currentMatchIndex={currentMatchIndex}
        />
      ))}
    </Stack>

    {canResumeAutoScroll ? (
      <Button
        aria-label="Resume autoscroll"
        size="small"
        startIcon={<DynamicIcon library="lucide" name="rotate-cw" size={14} />}
        onClick={(event) => {
          event.stopPropagation();
          onResumeAutoScroll?.();
        }}
        sx={callTranscriptResumeButtonSx}
      >
        Autoscroll
      </Button>
    ) : null}
  </Box>
);

const CallTranscriptSearchBar = ({
  searchQuery,
  totalMatches,
  currentMatchIndex,
  onSearchQueryChange,
  onPrevMatch,
  onNextMatch,
  onCloseSearch,
}: {
  searchQuery: string;
  totalMatches: number;
  currentMatchIndex: number;
  onSearchQueryChange: (query: string) => void;
  onPrevMatch: () => void;
  onNextMatch: () => void;
  onCloseSearch: () => void;
}) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const hasResults = totalMatches > 0;
  const resultsLabel = hasResults
    ? `${currentMatchIndex + 1} of ${totalMatches}`
    : 'No results';
  const countLabel = searchQuery ? resultsLabel : '';
  const navDisabled = totalMatches < 2;

  return (
    <Stack direction="row" sx={callTranscriptSearchBarSx}>
      <Box sx={callTranscriptSearchInputWrapSx}>
        <DynamicIcon
          name="search"
          sx={{ ...createCenteredIconSx(0.875), color: 'text.secondary' }}
        />
        <InputBase
          inputRef={searchInputRef}
          placeholder="Search transcript"
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCloseSearch();
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              if (totalMatches === 0) {
                return;
              }
              if (event.shiftKey) {
                onPrevMatch();
              } else {
                onNextMatch();
              }
            }
          }}
          sx={callTranscriptSearchInputSx}
        />
        {countLabel ? (
          <Typography component="span" sx={callTranscriptSearchCountSx(hasResults)}>
            {countLabel}
          </Typography>
        ) : null}
      </Box>
      <IconButton
        aria-label="Previous match"
        size="small"
        disabled={navDisabled}
        onClick={onPrevMatch}
        sx={callTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="expand_less" sx={callTranscriptHeaderToggleIconSx} />
      </IconButton>
      <IconButton
        aria-label="Next match"
        size="small"
        disabled={navDisabled}
        onClick={onNextMatch}
        sx={callTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="expand_more" sx={callTranscriptHeaderToggleIconSx} />
      </IconButton>
      <IconButton
        aria-label="Close search"
        size="small"
        onClick={onCloseSearch}
        sx={callTranscriptHeaderIconButtonSx}
      >
        <DynamicIcon name="close" sx={callTranscriptHeaderToggleIconSx} />
      </IconButton>
    </Stack>
  );
};

const useAiFollowUp = (
  attachment: ConversationAttachment & { url: string },
  enabled: boolean,
) => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const [isExpanded, setIsExpanded] = useState(false);
  const { aiPrompts, loading: isLoadingAiPrompts } = useAiPrompts({ skip: !enabled });
  const { getAiFollowUp, loading: isGeneratingFollowUp } = useAiEmailFollowUp();
  const [emailFollowUpContent, setEmailFollowUpContent] = useState('');
  const [userCorrection, setUserCorrection] = useState('');
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<AiPrompt | null>(null);
  const markdownContainerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { user } = useCurrentUserProfile();

  const transcriptionId = attachment.callTranscription?.transcriptionId;

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleGenerateFollowUp = useCallback(
    async (promptId?: string) => {
      if (!transcriptionId || !promptId || !user) {
        return;
      }

      const followUp = await getAiFollowUp({
        partnerId: attachment.partnerId,
        conversationId: attachment.conversationId,
        userId: user.id,
        transcriptionId,
        promptId,
      });
      setEmailFollowUpContent(followUp?.data?.getAiFollowUp.aiFollowUp || '');
      setIsExpanded(true);
    },
    [transcriptionId, attachment.conversationId, attachment.partnerId, getAiFollowUp, user],
  );

  const handleUserCorrectionSubmit = useCallback(async () => {
    if (!transcriptionId || !selectedPrompt?.promptId || !user) {
      return;
    }
    const correctedFollowUp = await getAiFollowUp({
      partnerId: attachment.partnerId,
      conversationId: attachment.conversationId,
      userId: user.id,
      transcriptionId,
      promptId: selectedPrompt.promptId,
      userCorrection,
    });
    setEmailFollowUpContent(correctedFollowUp?.data?.getAiFollowUp.aiFollowUp || '');
    setUserCorrection('');
  }, [
    transcriptionId,
    attachment.conversationId,
    attachment.partnerId,
    getAiFollowUp,
    selectedPrompt?.promptId,
    user,
    userCorrection,
  ]);

  const handleCopy = useCallback(async () => {
    // We need to be able to copy the generated response as rich text,
    // which is why we're using clipboard.write instead of clipboard.writeText
    const container = markdownContainerRef.current;
    if (!container) return;

    const html = container.innerHTML;
    const text = container.textContent;

    if (!html || !text) {
      return;
    }

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      showSuccessAlert('Copied!');
    } catch {
      showErrorAlert('Copy failed');
    }
  }, [showSuccessAlert, showErrorAlert]);

  const closeAutocomplete = useCallback(() => {
    triggerRef.current?.focus();
    setIsPopperOpen(false);
  }, []);

  const openAutocomplete = useCallback(() => {
    setIsPopperOpen(true);
  }, []);

  const onSelectPrompt = useCallback(
    (option: AiPrompt | null) => {
      setSelectedPrompt(option);
      void handleGenerateFollowUp(option?.promptId);
    },
    [handleGenerateFollowUp],
  );

  return {
    isReady: enabled && !isLoadingAiPrompts,
    isGenerating: isGeneratingFollowUp,
    isExpanded,
    triggerRef,
    isPopperOpen,
    openAutocomplete,
    closeAutocomplete,
    aiPrompts,
    onSelectPrompt,
    emailFollowUpContent,
    markdownContainerRef,
    userCorrection,
    setUserCorrection,
    handleUserCorrectionSubmit,
    handleCopy,
    handleClose,
  };
};

type AiFollowUp = ReturnType<typeof useAiFollowUp>;

const AiFollowUpPanel = ({ ai }: { ai: AiFollowUp }) => (
  <>
    <Collapse in={ai.isExpanded}>
      <Box px={1} pb={1}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Tooltip title="Copy">
            <IconButton onClick={ai.handleCopy}>
              <DynamicIcon name="content_copy" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={ai.handleClose}>
            <DynamicIcon name="close" />
          </IconButton>
        </Stack>

        <div ref={ai.markdownContainerRef} style={{ padding: 1, fontSize: '0.9rem' }}>
          <ReactMarkdown>{ai.emailFollowUpContent}</ReactMarkdown>
        </div>

        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Prompt correction..."
          value={ai.userCorrection}
          disabled={ai.isGenerating}
          onChange={(e) => ai.setUserCorrection(e.target.value)}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              void ai.handleUserCorrectionSubmit();
            }
          }}
          sx={{
            marginTop: 1,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={ai.handleUserCorrectionSubmit}>
                  <DynamicIcon name="send" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Collapse>

    <AutocompletePopper<AiPrompt>
      isOpen={ai.isPopperOpen}
      anchorEl={ai.triggerRef}
      closeAutocomplete={ai.closeAutocomplete}
      label="Select a prompt"
      placement="right-start"
      placeholder="Select a prompt"
      options={ai.aiPrompts}
      onChange={ai.onSelectPrompt}
      getOptionLabel={(option) => option.friendlyName}
      getOptionDisabled={(option) => !option.active}
    />
  </>
);

export const AudioAttachmentContainer = ({
  attachment,
  onAddToSiteFiles,
}: {
  attachment: ConversationAttachment & { url: string };
  onAddToSiteFiles?: () => void;
}) => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const isMultitenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const { groupRules } = useGroupRules();
  const { partnerGroupRules } = usePartnerGroupRules(partnerId);
  const hasAccessToAiAssistant =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];
  const canDownloadRecording =
    groupRules[UserGroupRules.AccessToDownloadCallRecordings] === true;

  const segments = useMemo(
    () => parseCallTranscriptSegments(attachment.callTranscription?.transcriptText),
    [attachment.callTranscription?.transcriptText],
  );
  const hasTranscript = segments.length > 0;
  const showAi =
    Boolean(attachment.callTranscription) && (hasAccessToAiAssistant || isMultitenancyMode);

  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const { copyToClipboard: copyTranscript } = useCopyToClipboard({
    successMessage: 'Transcript copied',
  });
  const { copyToClipboard: copyMessage } = useCopyToClipboard({
    successMessage: 'Message copied',
  });
  const ai = useAiFollowUp(attachment, showAi);
  const search = useCallTranscriptSearch(segments);
  const autoScroll = useCallTranscriptAutoScroll({
    scrollRef: search.scrollRef,
    segments,
    currentTimeSeconds,
    isPlaying,
    isTranscriptVisible,
    isSearchActive: search.isSearchOpen && Boolean(search.debouncedSearchQuery),
  });

  const showAiUi = showAi && ai.isReady;
  const menuOpen = Boolean(anchorEl);
  const showMenu = hasTranscript || canDownloadRecording || Boolean(onAddToSiteFiles);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddToSiteFiles = () => {
    handleMenuClose();
    onAddToSiteFiles?.();
  };

  const handleDownloadRecording = () => {
    handleMenuClose();
    void downloadFileFromUrl(
      attachment.url,
      attachment.fileName || `call-recording-${attachment.id}`,
    );
  };

  return (
    <Stack sx={callTranscriptBoxSx}>
      {search.isSearchOpen ? (
        <CallTranscriptSearchBar
          searchQuery={search.searchQuery}
          totalMatches={search.totalMatches}
          currentMatchIndex={search.currentMatchIndex}
          onSearchQueryChange={search.onSearchQueryChange}
          onPrevMatch={search.prevMatch}
          onNextMatch={search.nextMatch}
          onCloseSearch={search.closeSearch}
        />
      ) : (
        <Stack
          direction="row"
          sx={callTranscriptHeaderSx(isTranscriptVisible || ai.isExpanded)}
        >
          <Box flex={1} minWidth={0} overflow="hidden">
            <AudioPlayer
              audioUrl={attachment.url}
              onTimeUpdate={setCurrentTimeSeconds}
              onPlayingChange={setIsPlaying}
            />
          </Box>

          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {showAiUi ? (
              <Tooltip title="AI generation">
                <span>
                  <IconButton
                    ref={ai.triggerRef}
                    aria-label="AI generation"
                    size="small"
                    disabled={ai.isGenerating || !hasTranscript}
                    onClick={(e) => {
                      e.stopPropagation();
                      ai.openAutocomplete();
                    }}
                    sx={callTranscriptHeaderIconButtonSx}
                  >
                    {ai.isGenerating ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <DynamicIcon
                        filled
                        name={ai.isExpanded ? 'replay' : 'auto_awesome'}
                        sx={callTranscriptHeaderIconSx}
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}

            {isTranscriptVisible && hasTranscript ? (
              <Tooltip title="Search transcript">
                <IconButton
                  aria-label="Search transcript"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    search.openSearch();
                  }}
                  sx={callTranscriptHeaderIconButtonSx}
                >
                  <DynamicIcon name="search" sx={callTranscriptHeaderIconSx} />
                </IconButton>
              </Tooltip>
            ) : null}

            <Tooltip title={isTranscriptVisible ? 'Hide transcript' : 'Show transcript'}>
              <span>
                <IconButton
                  aria-label={isTranscriptVisible ? 'Hide transcript' : 'Show transcript'}
                  size="small"
                  disabled={!hasTranscript}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTranscriptVisible((prev) => !prev);
                  }}
                  sx={callTranscriptHeaderIconButtonSx}
                >
                  <DynamicIcon name="subtitles" sx={callTranscriptHeaderIconSx} />
                </IconButton>
              </span>
            </Tooltip>

            {showMenu ? (
              <>
                <IconButton
                  aria-label="More options"
                  size="small"
                  onClick={handleMenuOpen}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={callTranscriptHeaderIconButtonSx}
                >
                  <DynamicIcon name="more_vert" sx={callTranscriptHeaderIconSx} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
                  {canDownloadRecording ? (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadRecording();
                      }}
                    >
                      <DynamicIcon name="download" />
                      Download recording
                    </MenuItem>
                  ) : null}
                  {onAddToSiteFiles ? (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToSiteFiles();
                      }}
                    >
                      <DynamicIcon name="bookmark" />
                      Add to Site Files
                    </MenuItem>
                  ) : null}
                  {hasTranscript ? (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClose();
                        void copyTranscript(
                          attachment.callTranscription?.transcriptText ?? '',
                        );
                      }}
                    >
                      <DynamicIcon name="content_copy" />
                      Copy transcript
                    </MenuItem>
                  ) : null}
                </Menu>
              </>
            ) : null}
          </Stack>
        </Stack>
      )}

      <Collapse in={isTranscriptVisible}>
        <CallTranscriptionContainer
          segments={segments}
          scrollRef={search.scrollRef}
          onCopySegment={copyMessage}
          searchQuery={search.debouncedSearchQuery}
          currentMatchIndex={search.currentMatchIndex}
          matchOffsetBySegmentId={search.matchOffsetBySegmentId}
          onScroll={autoScroll.handleScroll}
          onUserScrollIntent={autoScroll.handleUserScrollIntent}
          canResumeAutoScroll={autoScroll.canResumeAutoScroll}
          onResumeAutoScroll={autoScroll.resumeAutoScroll}
        />
      </Collapse>

      {showAiUi ? <AiFollowUpPanel ai={ai} /> : null}
    </Stack>
  );
};

export const AttachmentMessageContent = ({
  attachment,
  compact = false,
}: {
  attachment: ConversationAttachment & { url: string };
  compact?: boolean;
}) => {
  const { user } = useCurrentUserProfile();
  const canAddToSiteFiles = useChatCommonData((s) => s.hasAssociatedAddresses);

  const { moveToSiteFiles } = useMoveAttachmentToSiteFiles();

  if (isSupportedAudioAttachmentContentType(attachment.contentType)) {
    return (
      <AudioAttachmentContainer
        attachment={attachment}
        onAddToSiteFiles={
          canAddToSiteFiles
            ? () =>
                moveToSiteFiles({
                  attachment,
                  uploadedByUserId: user?.id,
                })
            : undefined
        }
      />
    );
  }

  return (
    <Box display="flex" alignItems="stretch" height="100%">
      <Link
        href={attachment.url}
        key={attachment.id}
        target="_blank"
        underline="none"
        color="inherit"
        sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}
      >
        <FileAttachmentThumbnail
          attachment={attachment}
          compact={compact}
          draggableAttachment={
            canAddToSiteFiles ? getMoveAttachmentToSiteFilesPayload(attachment) : undefined
          }
          onAddToSiteFiles={
            canAddToSiteFiles
              ? () =>
                  moveToSiteFiles({
                    attachment,
                    uploadedByUserId: user?.id,
                  })
              : undefined
          }
        />
      </Link>
    </Box>
  );
};
